import { useState, useEffect, useCallback } from 'react'
import { evaluationService, type CreateTestSetRequest, type UpdateTestSetRequest, type TestSetQueryParams } from '@/services/evaluationService'
import type { TestSetWithStats, TestSetWithDetails } from '@shared/types/evaluation'
import { toast } from 'sonner'

interface UseTestSetsReturn {
  testSets: TestSetWithStats[]
  currentTestSet: TestSetWithDetails | null
  loading: boolean
  error: string | null
  createTestSet: (data: CreateTestSetRequest) => Promise<TestSetWithStats>
  updateTestSet: (id: string, data: UpdateTestSetRequest) => Promise<TestSetWithStats>
  deleteTestSet: (id: string) => Promise<void>
  refreshTestSets: () => Promise<void>
  refreshCurrentTestSet: () => Promise<void>
}

export const useTestSets = (currentTestSetId?: string): UseTestSetsReturn => {
  const [testSets, setTestSets] = useState<TestSetWithStats[]>([])
  const [currentTestSet, setCurrentTestSet] = useState<TestSetWithDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch all test sets
  const fetchTestSets = useCallback(async (params?: TestSetQueryParams) => {
    setLoading(true)
    setError(null)
    try {
      const data = await evaluationService.getTestSets(params)
      // Ensure data is an array
      setTestSets(Array.isArray(data) ? data : [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch test sets'
      setError(errorMessage)
      setTestSets([]) // Set empty array on error
      console.error('Error fetching test sets:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch specific test set
  const fetchCurrentTestSet = useCallback(async (testSetId: string) => {
    setLoading(true)
    setError(null)
    try {
      const data = await evaluationService.getTestSet(testSetId)
      setCurrentTestSet(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch test set'
      setError(errorMessage)
      console.error('Error fetching test set:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Create new test set
  const createTestSet = async (data: CreateTestSetRequest): Promise<TestSetWithStats> => {
    try {
      const newTestSet = await evaluationService.createTestSet(data)
      setTestSets(prev => [newTestSet, ...prev])
      toast.success('Test set created successfully')
      return newTestSet
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create test set'
      toast.error(errorMessage)
      throw err
    }
  }

  // Update test set
  const updateTestSet = async (id: string, data: UpdateTestSetRequest): Promise<TestSetWithStats> => {
    try {
      const updatedTestSet = await evaluationService.updateTestSet(id, data)
      
      // Update in list
      setTestSets(prev => prev.map(ts => ts.id === id ? updatedTestSet : ts))
      
      // Update current if it's the same one
      if (currentTestSet?.id === id) {
        setCurrentTestSet(updatedTestSet)
      }
      
      toast.success('Test set updated successfully')
      return updatedTestSet
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update test set'
      toast.error(errorMessage)
      throw err
    }
  }

  // Delete test set
  const deleteTestSet = async (id: string): Promise<void> => {
    try {
      await evaluationService.deleteTestSet(id)
      setTestSets(prev => prev.filter(ts => ts.id !== id))
      
      // Clear current if it's the same one
      if (currentTestSet?.id === id) {
        setCurrentTestSet(null)
      }
      
      toast.success('Test set deleted successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete test set'
      toast.error(errorMessage)
      throw err
    }
  }

  // Refresh functions
  const refreshTestSets = useCallback(() => fetchTestSets(), [fetchTestSets])
  const refreshCurrentTestSet = useCallback(() => {
    if (currentTestSetId) {
      return fetchCurrentTestSet(currentTestSetId)
    }
    return Promise.resolve()
  }, [currentTestSetId, fetchCurrentTestSet])

  // Effects
  useEffect(() => {
    fetchTestSets().catch(err => {
      console.error('Failed to fetch test sets on mount:', err)
    })
  }, [fetchTestSets])

  useEffect(() => {
    if (currentTestSetId) {
      fetchCurrentTestSet(currentTestSetId)
    } else {
      setCurrentTestSet(null)
    }
  }, [currentTestSetId, fetchCurrentTestSet])

  return {
    testSets,
    currentTestSet,
    loading,
    error,
    createTestSet,
    updateTestSet,
    deleteTestSet,
    refreshTestSets,
    refreshCurrentTestSet
  }
}